import { Box, Flex, Spacer, Text, useColorModeValue } from "@chakra-ui/react";
import { ResponsiveLine } from "@nivo/line";
import { Card } from "../../../components/Card";
import { NumberField } from "../../../components/NumberField";
import { numberFormatter } from "../../../utils";
import { useTranslation } from "react-i18next";

function DecarbonizationChart({
                                data = [],
                                legends,
                                tickMultiplier = 5,
                                chartMargin = { top: 10, right: 120, bottom: 50, left: 60 },
                                isDataAssociated = false,
                                ...rest
                              }) {
  const { t } = useTranslation("translation", { keyPrefix: "decarbonization.chart" });
  const chartTextColor = useColorModeValue("black", "white");
  const chartBgColor = useColorModeValue("white", "black");
  const chartGridColor = useColorModeValue("gainsboro", "slategrey");
  const filledAreas = ["Net Zero 2050", "SBTI 2030", "25% Reduction", "90% Reduction"];

  const getTickValues = () => {
    // We include base year as first value on X axis, max year as last value and
    // all the multiples of {{tickMultiplier}} in the range (baseYear, maxYear)
    // that have a difference with base and max year >= 1
    const { baseYear, maxYear } = data;
    const ticksArray = [new Date(`01/01/${baseYear}`)];
    for (let year = Number(baseYear) + 1; year < maxYear; year++) {
      if (year % tickMultiplier === 0 && year - baseYear >= 1 && maxYear - year >= 1) {
        ticksArray.push(new Date(`01/01/${year}`));
      }
    }
    ticksArray.push(new Date(`01/01/${maxYear}`));
    return ticksArray;
  };


  const Areas = ({
                   series, areaGenerator, xScale, yScale
                 }) => series.map(({ id, data, color }) => (
    <path
      key={id}
      d={areaGenerator(
        data.map(d => ({
          x: xScale(d.data.x),
          y: yScale(d.data.y)
        }))
      )}
      fill={filledAreas.includes(id) ? color : "none"}
      fillOpacity={0.25}
    />
  ));


  // Adding width 99% to fix issue https://github.com/plouc/nivo/issues/411
  // Implemented this solution https://github.com/plouc/nivo/issues/411#issuecomment-761939245
  return (
    <Card blockSize="60" inlineSize="99%" padding={6} height={630} {...rest}>
      <Flex marginBottom={5}>
        <Box>
          <Text fontSize="sm" color="esg_gray.300">
            {data.companyName ? `${data.companyName} ${t("baseYear")}` : t("baseYear")}
          </Text>
          <Text fontSize="lg">{data.baseYear}</Text>
        </Box>
        <Spacer />
        <Box>
          <Text fontSize="sm" color="esg_gray.300">
            {isDataAssociated ? `${t("associated")} ` : ""}{t("baselineEmissions")}
          </Text>
          <Flex gap={1}>
            <NumberField fontSize="lg" value={Math.round(data.baselineEmissionsTotal)} />
            <Text>(tCO<sub>2e</sub>)</Text>
          </Flex>
        </Box>
      </Flex>
      <ResponsiveLine
        data={data.chartData}
        height={500}
        margin={chartMargin}
        colors={d => d.color}
        layers={["grid", "markers", "axes", Areas, "crosshair", "lines", "points", "slices", "mesh", "legends"]}
        sliceTooltip={point => {
          return (
            <Card p={3}>
              {
                point?.slice?.points?.sort((a, b) => {
                  return (a.data.y > b.data.y) ? -1 : ((b.data.y > a.data.y) ? 1 : 0);
                }).map((item, itemIndex) => {
                  return (<Flex key={itemIndex} gap={1}>
                    <Box bg={item?.serieColor} width={3} height={3} marginBlockStart={1.5} />
                    <Text>{item.serieId} -</Text>
                    <Text>{t("year")}: {item.data.xFormatted},</Text>
                    <Text>
                      {t("emissions")}: {Math.round(item.data.yFormatted).toLocaleString("en-GB", { maximumFractionDigits: 0 })} tCO<sub>2e</sub>
                    </Text>
                  </Flex>);
                })
              }
            </Card>);
        }}

        xFormat="time:%Y"
        xScale={{
          type: "time",
          format: "%Y",
          precision: "year"
        }}
        yScale={{
          type: "linear",
          stacked: false,
          min: 0,
          max: data.maxValue
        }}

        axisLeft={{
          orient: "left",
          tickSize: 0,
          tickPadding: 10,
          tickRotation: 0,
          legend: t("tonnesEmissions"),
          legendOffset: -43,
          legendPosition: "middle",
          format: value => numberFormatter(value, 0)
        }}
        axisBottom={{
          orient: "bottom",
          tickSize: 0,
          tickPadding: 10,
          tickRotation: 0,
          format: "%Y",
          tickValues: getTickValues(),
          legend: t("year"),
          legendOffset: 40,
          legendPosition: "middle"
        }}

        pointBorderWidth={1}
        pointLabelYOffset={-12}
        pointSize={6}
        pointBorderColor={{
          from: "color",
          modifiers: [["darker", 0.3]]
        }}
        useMesh={true}
        enableGridY={false}
        gridXValues={getTickValues()}
        enableSlices="x"
        theme={{
          textColor: chartTextColor,
          grid: {
            line: {
              stroke: chartGridColor
            }
          },
          tooltip: {
            container: {
              background: chartBgColor,
              color: chartTextColor
            }
          }
        }}
        legends={legends}
      />
    </Card>
  );
}

export default DecarbonizationChart;